import { Entity } from '@api';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useTabsNavigation } from './tabs/hooks/useTabsNavigation';
import { ParentCategoryTab } from './tabs/parent-category-tab/ParentCategoryTab';
import { ThemeExposureTab } from './tabs/theme-exposure-tab/ThemeExposureTab';
import { ThemeOverviewTab } from './tabs/theme-overview-tab/ThemeOverviewTab';
import styles from './ThemeWidget.module.scss';

export enum SubTabsType {
  ThemeExposure = 'themeExposure',
  ThemeOverview = 'themeOverview',
  ParentCategory = 'parentCategory',
  CompetitorOverview = 'competitorOverview',
  CompetitorDetail = 'competitorDetail',
}

export interface ThemeWidgetProps {
  entity: Entity;
  themeId: string;
}

export const ThemeWidget = ({ entity, themeId }: ThemeWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();

  const { data, isLoading, isError, error } = useCompanyTheme({
    entityTag: entity.tag,
  });
  const theme = data?.find(t => t.theme.id === themeId);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loaderWrapper} data-testid="theme-loader">
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '40px', borderRadius: '8px' },
              },
            ]}
          />
          <SkeletonLoader
            areas={[
              { styles: { width: '65%', height: '32px', borderRadius: '8px' } },
            ]}
          />
          <SkeletonLoader
            areas={[
              { styles: { width: '55%', height: '32px', borderRadius: '8px' } },
            ]}
          />
        </div>
      );
    }

    if (isError) {
      throw new Error(error?.message);
    }

    if (!theme) {
      return null;
    }

    const commonProps = {
      entity,
      theme,
    };

    switch (params[queryKeys.activeDrawerTab]) {
      case SubTabsType.ThemeOverview:
        return (
          <ThemeOverviewTab
            {...commonProps}
            source={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER}
            title={t(
              'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeOverview.overview'
            )}
          />
        );
      case SubTabsType.ParentCategory:
        return (
          <ParentCategoryTab
            {...commonProps}
            source={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER}
          />
        );
      default:
        return <ThemeExposureTab {...commonProps} />;
    }
  };

  const subTabs = [
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.title'
      ),
      key: SubTabsType.ThemeExposure,
    },
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeOverview.title'
      ),
      key: SubTabsType.ThemeOverview,
    },
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.themeExposure.tabs.parentCategory.title'
      ),
      key: SubTabsType.ParentCategory,
    },
  ];
  const { activeIndex, onTabChange } = useTabsNavigation(
    subTabs,
    SubTabsType.ThemeExposure
  );

  if (!themeId) {
    return null;
  }

  return (
    <div className={styles.container}>
      <ButtonGroup
        className={styles.styledButtonGroup}
        labels={subTabs.map(t => t.label)}
        activeIndex={activeIndex}
        onClick={onTabChange}
      />
      <ErrorBoundaryWrapper>{renderContent()}</ErrorBoundaryWrapper>
    </div>
  );
};
